import {useCallback, useEffect, useState} from "react";
import {
    Badge, Banner,
    Button,
    FormLayout,
    IndexTable,
    LegacyCard,
    Modal, OptionList,
    Select, Spinner,
    Text,
    useIndexResourceState
} from "@shopify/polaris";
import Api from "../../helpers/api";
import {useToast} from "@shopify/app-bridge-react";
import {formatDate, formatDateToUsFormat} from "../Dates";
import {str_slug} from "../../helpers/utils";
import {cloneDeep} from "lodash";
import {OrderPaymentStatusBadge} from "./OrderPaymentStatusBadge";

const stripe = Stripe(window.stripe_key);

export const CustomerOrders = ({
    customer,
}) => {
    const [loading, setLoading] = useState(false);

    const [orders, setOrders] = useState({data: [], meta: {current_page: 1, last_page: 1, total: 0}});
    const toast = useToast();
    const {selectedResources, allResourcesSelected, handleSelectionChange} = useIndexResourceState([]);
    const [downloading, setDownloading] = useState(false);
    const [modalActive, setModalActive] = useState(false);

    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = useCallback((page = 1) => {
        setLoading(true);
        Api.getInvoices(customer.id, page)
            .then(({data}) => setOrders(data))
            .catch(() => toast.show("Server Error, please try again"))
            .finally(() => setLoading(false));
    }, [customer]);

    const payForOrders = useCallback(() => {
        if (selectedResources.length === 0) {
            toast.show("You need to select at least one Order");
            return;
        }

        setModalActive(true);
    }, [selectedResources]);

    const downloadFile = function (data, title){
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${title}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    };

    const downloadInvoice = useCallback((id, orderNumber) => {
        setDownloading(true);
        Api.downloadInvoice(customer.id, id)
            .then((response) => downloadFile(response.data, str_slug(orderNumber)))
            .finally(() => setDownloading(false))
    }, [customer]);

    const resourceName = {
        singular: 'order',
        plural: 'orders',
    };

    return (<div style={{marginBottom: "20px", marginTop: "20px"}}><LegacyCard title={"Company Orders"} actions={[{content: "Refresh", onAction(){ loadOrders(orders.meta.current_page)}, disabled: loading}]}>
        <IndexTable
            resourceName={resourceName}
            headings={[
                {title: 'Order'},
                {title: 'Date'},
                {title: 'Total'},
                {title: 'Payment'},
                {title: 'PO Number'},
                {title: ''}
            ]}
            selectable
            loading={loading}
            itemCount={orders.meta.total}
            selectedItemsCount={selectedResources.length}
            promotedBulkActions={[
                {
                    content: "Pay for Orders",
                    onAction: payForOrders
                }
            ]}
            pagination={{
                hasNext: orders.meta.current_page - 1 > 0,
                onNext: () => loadOrders(orders.meta.current_page - 1),
                hasPrevious: orders.meta.current_page + 1 <= orders.meta.last_page,
                onPrevious: () => loadOrders(orders.meta.current_page + 1),
                label: `Page ${orders.meta.current_page} of ${orders.meta.last_page}`
            }}
            onSelectionChange={handleSelectionChange}
        >
            {orders.data.map((order, index) => {
                return (<IndexTable.Row
                    id={order.id}
                    position={index}
                    key={index}
                    selected={selectedResources.includes(order.id)}
                >
                    <IndexTable.Cell ><Text as={'div'} fontWeight={'bold'}>{order.order_number}</Text></IndexTable.Cell>
                    <IndexTable.Cell>{formatDateToUsFormat(order.shopify_created_at)}</IndexTable.Cell>
                    <IndexTable.Cell>{`$${order.additional_data.total_price}`}</IndexTable.Cell>
                    <IndexTable.Cell><OrderPaymentStatusBadge order={order} /> </IndexTable.Cell>
                    <IndexTable.Cell>{ order.additional_data.note_attributes?.find(attr => attr.name == '_PO_NUMBER')?.value ?? ''}</IndexTable.Cell>
                    <IndexTable.Cell><Button loading={downloading} onClick={() => downloadInvoice(order.id, order.order_number)}>Invoice</Button></IndexTable.Cell>
                </IndexTable.Row>)
            })}
        </IndexTable>
        <BulkPayModal
            customer={customer}
            modalActive={modalActive}
            setModalActive={setModalActive}
            selected={selectedResources}
            loadOrders={() => loadOrders(orders.meta.current_page)}
        />
    </LegacyCard></div>);
}

const BulkPayModal = ({customer, modalActive, setModalActive, selected, loadOrders}) => {
    const [loading, setLoading] = useState(false);
    const [selectedCard, setSelectedCard] = useState(customer.card?.id?.toString() ?? null);
    const toast = useToast();
    const [method, setMethod] = useState('credit_card');

    const chargeOrders = useCallback(() => {
        if (method != 'credit_card') {
            document.getElementById('payment-form').requestSubmit();
            return;
        }

        setLoading(true);
        Api.chargeOrders({
            customer_id: customer.id,
            payment_method_id: selectedCard,
            selected_orders: selected,
        })
            .then(() => {
                toast.show("Orders have been charged.");
                setModalActive(false);
            })
            .catch((e) => {
                if (e.response.status == 406 && e.response.data.error) {
                    toast.show(e.response.data.error, {isError: true});
                } else {
                    toast.show("There was error while trying to charge orders.", {isError: true});
                }
            })
            .finally(() => {
                loadOrders();
                setLoading(false);
            });
    }, [selectedCard, selected, method]);

    const onClose = useCallback(() => {
        if (!loading) {
            setModalActive(false);
        }
    }, [loading]);

    return (<Modal
        open={modalActive}
        title={`Pay for ${selected.length} orders`}
        onClose={onClose}
        primaryAction={{
            content: "Charge",
            onAction: chargeOrders,
            loading: loading,
        }}
        secondaryActions={[
            {
                content: "Cancel",
                onAction: onClose,
                disabled: loading,
            }
        ]}
        size={"large"}
    >
        <Modal.Section>
            <FormLayout>
                <OptionList selected={method} onChange={setMethod} options={[
                    {value: 'credit_card', 'label': 'Company Credit Card'},
                    {value: 'other', 'label': 'Other (ACH)'}
                ]} />
                {customer.cards.length === 0 && method == 'credit_card' && (<Banner tone={"warning"}>Company does not have any Credit Cards.</Banner>)}

                {customer.cards.length > 0 && method == 'credit_card' && (<Select
                    label={"Credit Card"}
                    placeholder={"Select Credit Card"}
                    value={selectedCard}
                    onChange={(val) => {
                        setSelectedCard(val);
                    }}
                    options={customer.cards.map(card => {return {
                        label: `${card.additional_data.card} ${card.additional_data.exp_date}`,
                        value: card.id.toString(),
                    }})}
                />)}

                {method == 'other' && (<OtherPayments
                    customer={customer}
                    selected={selected}
                    loading={loading}
                    setLoading={setLoading}
                    loadOrders={loadOrders}
                />)}
            </FormLayout>
        </Modal.Section>
    </Modal>)
}

const OtherPayments = ({
    customer,
    selected,
    loading,
    setLoading,
    loadOrders,
}) => {
    const [secret, setSecret] = useState(null);
    const [url, setUrl] = useState(null);
    const toast = useToast();

    useEffect(() => {
        setLoading(true);

        Api.getPaymentIntentForOrders({
            customer_id: customer.id,
            selected_orders: selected,
        })
            .then(({data}) => {
                setSecret(data.clientSecret);
                setUrl(data.url);
                init(data);
            })
            .catch((e) => {
                if (e.response.status == 406 && e.response.data.error) {
                    toast.show(e.response.data.error, {isError: true});
                } else {
                    toast.show("Error while trying to get Payment Intent. Please try again.", {isError: true});
                }
            })
            .finally(() => {
                loadOrders();
                setLoading(false);
            });
    }, []);

    const init = useCallback(({clientSecret, url}) => {
        const stripeElements = stripe.elements({ clientSecret });
        const stripeElement = stripeElements.create('payment', {
            layout: "tabs",
        });
        stripeElement.mount("#payment-element");

        setTimeout(() => {
            document
                .querySelector("#payment-form")
                .addEventListener("submit", (e) =>  handleSubmit(e, stripeElements, url));
        }, 200)
    }, [secret]);

    const handleSubmit = (e, stripeElements, url) => {
        e.preventDefault();
        setLoading(true);

        stripe.confirmPayment({
            elements: stripeElements,
            confirmParams: {
                return_url: url,
                receipt_email: customer.email,
            },
        })
            .then((data) => {
                if (data.error.type === "card_error" || data.error.type === "validation_error") {
                    alert(data.error.message);
                } else {
                    alert("An unexpected error occurred.");
                }
            })
            .catch(error => {
                console.log(error);
                alert("An unexpected error occurred.");
            })
            .finally(() => setLoading(false));
    }

    return (<>
        {loading && (<Spinner size={'small'} />)}
        <form id="payment-form">
            <div id="payment-element"></div>
        </form>
    </>);
}
